import { Link, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { useState, useCallback } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useProfileQuery } from "@/hooks/useProfileQuery";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User, Settings, LogOut } from "lucide-react";

interface EditorialNavProps {
  onSmoothScroll?: (e: React.MouseEvent<HTMLAnchorElement>, id: string) => void;
}

const navLinks = [
  { label: "The Method", href: "/#features", scrollId: "features" },
  { label: "How it Works", href: "/#how-it-works", scrollId: "how-it-works" },
  { label: "Pricing", href: "/pricing", scrollId: null },
  { label: "FAQ", href: "/#faq", scrollId: "faq" },
];

export const EditorialNav = ({ onSmoothScroll }: EditorialNavProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { data: profile } = useProfileQuery();

  const handleScroll = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        setOpen(false);
        setTimeout(() => target.scrollIntoView({ behavior: "smooth" }), 100);
      }
      onSmoothScroll?.(e, id);
    },
    [onSmoothScroll]
  );

  const initials = profile?.company_name
    ? profile.company_name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.[0]?.toUpperCase() || "U";

  return (
    <nav className="fixed top-4 md:top-6 inset-x-0 z-50 px-4 md:px-6">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4 py-2.5 md:py-3 pl-5 pr-3 md:pl-6 md:pr-3 atrium-glass rounded-full shadow-[0_8px_32px_rgba(21,34,27,0.06)]">
        <div className="flex items-center gap-8">
          <Logo to="/" size="sm" />

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
              link.scrollId ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleScroll(e, link.scrollId!)}
                  className="flex items-center text-sm font-medium leading-none text-foreground/65 hover:text-foreground transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="flex items-center text-sm font-medium leading-none text-foreground/65 hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1.5 hover:bg-secondary transition-colors">
                  <Avatar className="h-7 w-7 rounded-full">
                    <AvatarFallback className="bg-primary text-primary-foreground text-[10px] rounded-full font-medium">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-foreground">
                    {profile?.company_name || "Account"}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-card border-border rounded-2xl shadow-[0_20px_40px_-10px_rgba(21,34,27,0.12)]">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold text-foreground">{profile?.company_name || "My Account"}</div>
                  <div className="text-xs text-muted-foreground font-normal mt-0.5">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer text-sm rounded-lg">
                  <User className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")} className="cursor-pointer text-sm rounded-lg">
                  <Settings className="mr-2 h-4 w-4" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem onClick={signOut} className="cursor-pointer text-sm rounded-lg text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <button
                onClick={() => navigate("/auth")}
                className="text-sm font-medium px-4 py-2 rounded-full text-foreground/75 hover:bg-secondary hover:text-foreground transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate("/auth?mode=signup")}
                className="text-sm font-medium px-5 py-2.5 bg-primary text-primary-foreground rounded-full shadow-[0_4px_14px_rgba(62,92,77,0.18)] hover:shadow-[0_6px_20px_rgba(62,92,77,0.25)] hover:-translate-y-px transition-all"
              >
                Start Free Trial
              </button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="flex md:hidden items-center gap-2">
          {!user && (
            <button
              onClick={() => navigate("/auth?mode=signup")}
              className="text-xs font-medium px-4 py-2 bg-primary text-primary-foreground rounded-full shadow-[0_4px_14px_rgba(62,92,77,0.18)]"
            >
              Start free
            </button>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="h-10 w-10 flex items-center justify-center text-foreground rounded-full hover:bg-secondary transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[320px] bg-background border-border p-0 rounded-l-3xl">
              <div className="flex flex-col h-full p-8">
                <div className="mb-10">
                  <Logo to="/" size="md" />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  {navLinks.map((link) =>
                    link.scrollId ? (
                      <a
                        key={link.label}
                        href={link.href}
                        onClick={(e) => handleScroll(e, link.scrollId!)}
                        className="text-2xl font-medium text-foreground py-3 hover:text-primary transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        key={link.label}
                        to={link.href}
                        onClick={() => setOpen(false)}
                        className="text-2xl font-medium text-foreground py-3 hover:text-primary transition-colors"
                      >
                        {link.label}
                      </Link>
                    )
                  )}
                </div>
                <div className="mt-auto flex flex-col gap-3 pt-6 border-t border-border">
                  {user ? (
                    <>
                      <button
                        onClick={() => { navigate("/dashboard"); setOpen(false); }}
                        className="px-6 py-3.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-secondary transition-colors"
                      >
                        Dashboard
                      </button>
                      <button
                        onClick={() => { signOut(); setOpen(false); }}
                        className="px-6 py-3.5 rounded-full bg-primary text-primary-foreground text-sm font-medium"
                      >
                        Sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { navigate("/auth"); setOpen(false); }}
                        className="px-6 py-3.5 rounded-full border border-border text-sm font-medium text-foreground hover:bg-secondary transition-colors"
                      >
                        Sign in
                      </button>
                      <button
                        onClick={() => { navigate("/auth?mode=signup"); setOpen(false); }}
                        className="px-6 py-3.5 rounded-full bg-primary text-primary-foreground text-sm font-medium shadow-[0_4px_14px_rgba(62,92,77,0.18)]"
                      >
                        Start Free Trial
                      </button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
